/**
 * BrightPath - useExpenses Hook
 * BP-04-04: Expenses Frontend
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expensesApi } from '../api';
import type {
  Expense,
  ExpenseCreate,
  ExpenseUpdate,
  ExpenseListParams,
  ExpenseListResponse,
  ExpenseSummary,
  ExpenseTrend,
  CategorySummary,
  ChildSummary,
} from '../types';

// Query keys
export const expenseKeys = {
  all: ['expenses'] as const,
  lists: () => [...expenseKeys.all, 'list'] as const,
  list: (familyId: string, params?: ExpenseListParams) =>
    [...expenseKeys.lists(), familyId, params] as const,
  details: () => [...expenseKeys.all, 'detail'] as const,
  detail: (expenseId: string) => [...expenseKeys.details(), expenseId] as const,
  summaries: () => [...expenseKeys.all, 'summary'] as const,
  summary: (familyId: string, params?: Record<string, unknown>) =>
    [...expenseKeys.summaries(), familyId, params] as const,
  byCategory: (familyId: string, params?: Record<string, unknown>) =>
    [...expenseKeys.all, 'by-category', familyId, params] as const,
  byChild: (familyId: string, params?: Record<string, unknown>) =>
    [...expenseKeys.all, 'by-child', familyId, params] as const,
  trends: (familyId: string, params?: Record<string, unknown>) =>
    [...expenseKeys.all, 'trends', familyId, params] as const,
};

/**
 * Hook for fetching expenses list with filtering and pagination
 */
export function useExpenses(familyId: string, params?: ExpenseListParams) {
  return useQuery({
    queryKey: expenseKeys.list(familyId, params),
    queryFn: () => expensesApi.getExpenses(familyId, params),
    enabled: !!familyId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook for fetching a single expense
 */
export function useExpense(expenseId: string) {
  return useQuery({
    queryKey: expenseKeys.detail(expenseId),
    queryFn: () => expensesApi.getExpense(expenseId),
    enabled: !!expenseId,
  });
}

/**
 * Hook for expense summary statistics
 */
export function useExpenseSummary(
  familyId: string,
  params?: {
    startDate?: string;
    endDate?: string;
    year?: number;
    month?: number;
  }
) {
  return useQuery({
    queryKey: expenseKeys.summary(familyId, params),
    queryFn: () => expensesApi.getExpenseSummary(familyId, params),
    enabled: !!familyId,
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Hook for expenses by category breakdown
 */
export function useExpensesByCategory(
  familyId: string,
  params?: {
    startDate?: string;
    endDate?: string;
    year?: number;
  }
) {
  return useQuery({
    queryKey: expenseKeys.byCategory(familyId, params),
    queryFn: () => expensesApi.getExpensesByCategory(familyId, params),
    enabled: !!familyId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook for expenses by child breakdown
 */
export function useExpensesByChild(
  familyId: string,
  params?: {
    startDate?: string;
    endDate?: string;
    year?: number;
  }
) {
  return useQuery({
    queryKey: expenseKeys.byChild(familyId, params),
    queryFn: () => expensesApi.getExpensesByChild(familyId, params),
    enabled: !!familyId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook for expense trends over time
 */
export function useExpenseTrends(
  familyId: string,
  params?: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'day' | 'week' | 'month';
  }
) {
  return useQuery({
    queryKey: expenseKeys.trends(familyId, params),
    queryFn: () => expensesApi.getExpenseTrends(familyId, params),
    enabled: !!familyId,
    staleTime: 60 * 1000,
  });
}

/**
 * Hook for creating an expense
 */
export function useCreateExpense(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ExpenseCreate) => expensesApi.createExpense(familyId, data),
    onSuccess: () => {
      // Invalidate expense list and summaries
      queryClient.invalidateQueries({ queryKey: expenseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expenseKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: ['budgets'] }); // Also update budget status
    },
  });
}

/**
 * Hook for updating an expense
 */
export function useUpdateExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId, data }: { expenseId: string; data: ExpenseUpdate }) =>
      expensesApi.updateExpense(expenseId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.detail(variables.expenseId) });
      queryClient.invalidateQueries({ queryKey: expenseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expenseKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
    },
  });
}

/**
 * Hook for deleting an expense
 */
export function useDeleteExpense() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (expenseId: string) => expensesApi.deleteExpense(expenseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expenseKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
    },
  });
}

/**
 * Hook for bulk creating expenses
 */
export function useBulkCreateExpenses(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (expenses: ExpenseCreate[]) =>
      expensesApi.bulkCreateExpenses(familyId, expenses),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expenseKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
    },
  });
}

/**
 * Hook for bulk deleting expenses
 */
export function useBulkDeleteExpenses(familyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (expenseIds: string[]) =>
      expensesApi.bulkDeleteExpenses(familyId, expenseIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expenseKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
    },
  });
}

/**
 * Hook for importing expenses from CSV
 */
export function useImportExpenses(familyId: string) {
  const queryClient = useQueryClient();

  const previewMutation = useMutation({
    mutationFn: (file: File) => expensesApi.importExpensesPreview(familyId, file),
  });

  const confirmMutation = useMutation({
    mutationFn: ({
      file,
      options,
    }: {
      file: File;
      options?: { skipDuplicates?: boolean; defaultCategory?: string };
    }) => expensesApi.importExpensesConfirm(familyId, file, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expenseKeys.summaries() });
      queryClient.invalidateQueries({ queryKey: ['budgets'] });
    },
  });

  return {
    preview: previewMutation,
    confirm: confirmMutation,
  };
}
